import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { solutions, industries } from "../mock";

export const Logo = ({ dark = false }) => (
  <Link to="/" className="leading-none select-none flex items-baseline gap-1" data-testid="logo">
    <span className={`text-[26px] font-extrabold tracking-tight ${dark ? "text-[#071d3f]" : "text-white"}`} style={{ fontFamily: "Archivo, sans-serif" }}>
      ZOOFY
    </span>
    <span className="text-[26px] font-extrabold tracking-tight text-white" style={{ fontFamily: "Archivo, sans-serif" }}>
      TECHX
    </span>
  </Link>
);

const Dropdown = ({ label, items, wide }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center gap-1 text-[15px] font-medium text-white/90 hover:text-white py-6 transition-colors">
        {label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-1 transition-all duration-200 ${
          open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
        }`}
      >
        <div className={`bg-[#071d3f] border border-white/15 rounded-xl shadow-2xl shadow-black/50 p-3 ${wide ? "w-[560px] grid grid-cols-2 gap-1" : "w-[280px]"}`}>
          {items.map((item) => (
            <button
              key={item.slug || item.label}
              onClick={() => { setOpen(false); navigate(item.link); }}
              className="w-full text-left px-4 py-2.5 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-between group"
            >
              <span>{item.title || item.name || item.label}</span>
              <ArrowRight size={13} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-white" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#050f24]/95 backdrop-blur-md shadow-lg shadow-black/20" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          <Dropdown label="What We Do" wide items={solutions.map((s) => ({ ...s, link: `/solutions/${s.slug}` }))} />
          <Dropdown label="Industries" items={industries.map((i) => ({ ...i, link: `/industries/${i.slug}` }))} />
          <Link to="/about" className="text-[15px] font-medium text-white/90 hover:text-white transition-colors">Who We Are</Link>
          <Link to="/careers" className="text-[15px] font-medium text-white/90 hover:text-white transition-colors">Careers</Link>
          <Link
            to="/contact"
            className="text-[15px] font-semibold text-[#04122e] bg-white hover:bg-[#d7e3f4] px-5 py-2 rounded-full transition-colors"
          >
            Contact Us
          </Link>
        </nav>
        <button className="lg:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu">
          {mobileOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#050f24] border-t border-white/10 px-6 py-6 max-h-[80vh] overflow-y-auto">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2">What We Do</p>
          {solutions.map((s) => (
            <Link key={s.slug} to={`/solutions/${s.slug}`} className="block py-2 text-white/85 text-[15px]">{s.title}</Link>
          ))}
          <p className="text-xs uppercase tracking-widest text-white/40 mt-4 mb-2">Industries</p>
          {industries.map((i) => (
            <Link key={i.slug} to={`/industries/${i.slug}`} className="block py-2 text-white/85 text-[15px]">{i.name}</Link>
          ))}
          <div className="border-t border-white/10 mt-4 pt-4 space-y-1">
            <Link to="/about" className="block py-2 text-white font-medium">Who We Are</Link>
            <Link to="/careers" className="block py-2 text-white font-medium">Careers</Link>
            <Link to="/contact" className="block py-2 text-white font-semibold">Contact Us</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
